import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Download, Search, X } from "lucide-react";
import type { CueScript } from "../types";
import type { BatchExportFormat } from "../studio/batch-export";
import { LibrarySearch } from "../core/library-search";
import "./batch-export.css";

export function BatchExportDialog({ scripts, onExport, onClose, returnFocus }: {
  scripts: CueScript[];
  onExport: (ids: string[], format: BatchExportFormat, options: { signal: AbortSignal; onProgress: (done: number, total: number) => void }) => Promise<void>;
  onClose: () => void;
  returnFocus: HTMLElement | null;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  const selectAll = useRef<HTMLInputElement>(null);
  const task = useRef<AbortController | null>(null);
  const close = useRef(onClose);
  close.current = onClose;
  const headingId = useId();
  const descriptionId = useId();
  const formatId = useId();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [format, setFormat] = useState<BatchExportFormat>("docx");
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState("");
  const search = useMemo(() => new LibrarySearch(), []);
  const results = useMemo(() => search.search(scripts, query), [search, scripts, query]);
  const selectedScripts = useMemo(() => scripts.filter(s => selected.has(s.id)), [scripts, selected]);
  const allChecked = results.length > 0 && results.every(({ script }) => selected.has(script.id));
  const someChecked = results.some(({ script }) => selected.has(script.id));
  const busy = progress !== null;
  const count = selectedScripts.length;

  useEffect(() => {
    const element = dialog.current!;
    // macOS WebKit leaves mouse-clicked buttons unfocused. Restore the actual
    // opener rather than whichever editor/input happened to be focused before it.
    const previous = returnFocus ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    const cancel = (event: Event) => { event.preventDefault(); close.current(); };
    element.addEventListener("cancel", cancel);
    element.showModal();
    searchInput.current?.focus();
    return () => {
      task.current?.abort();
      element.removeEventListener("cancel", cancel);
      element.close();
      if (previous?.isConnected) previous.focus();
    };
  }, []);
  useEffect(() => {
    if (selectAll.current) selectAll.current.indeterminate = someChecked && !allChecked;
  }, [allChecked, someChecked]);

  const exportSelected = async () => {
    if (!count || task.current) return;
    const controller = new AbortController();
    task.current = controller;
    setError("");
    setProgress({ done: 0, total: count });
    try {
      await onExport(selectedScripts.map(s => s.id), format, {
        signal: controller.signal,
        onProgress: (done, total) => { if (!controller.signal.aborted) setProgress({ done, total }); },
      });
      if (!controller.signal.aborted) close.current();
    } catch (cause) {
      if (!controller.signal.aborted) {
        setError(cause instanceof Error ? cause.message : "Export could not finish. Try again or choose plain text.");
      }
    } finally {
      if (!controller.signal.aborted) setProgress(null);
      if (task.current === controller) task.current = null;
    }
  };

  return (
    <dialog ref={dialog} className="batch-export-dialog" aria-labelledby={headingId} aria-describedby={descriptionId}
      onKeyDown={event => event.stopPropagation()}>
      <header className="batch-export-header">
        <div>
          <h2 id={headingId}>Batch export</h2>
          <p id={descriptionId}>Choose scripts, then pick a file format.</p>
        </div>
        <button type="button" className="icon-button" aria-label="Close batch export" data-help="Close batch export. An export still being prepared will be canceled." onClick={onClose}>
          <X size={18} aria-hidden="true" />
        </button>
      </header>
      <div className="batch-export-selection">
        <label className="batch-export-search">
          <Search size={16} aria-hidden="true" />
          <input ref={searchInput} type="search" aria-label="Find scripts to export" placeholder="Search titles, tags or text" maxLength={300}
            value={query} disabled={busy} onChange={event => setQuery(event.target.value)} />
        </label>
        <div className="batch-export-selection-bar">
          <label>
            <input ref={selectAll} type="checkbox" checked={allChecked} disabled={busy || !results.length}
              data-help="Select or deselect every script shown below. Scripts selected before searching remain selected."
              onChange={event => {
                const checked = event.target.checked;
                setSelected(previous => {
                  const next = new Set(previous);
                  for (const { script } of results) { if (checked) next.add(script.id); else next.delete(script.id); }
                  return next;
                });
              }} />
            {query.trim() ? "Select all results" : "Select all"}
          </label>
          <span role="status" data-help="The number of selected scripts, including any hidden by your search." tabIndex={0}>{count} selected</span>
          {count > 0 && <button type="button" className="subtle-button" disabled={busy} data-help="Deselect all scripts, including those hidden by your search." onClick={() => setSelected(new Set())}>Clear selection</button>}
        </div>
      </div>
      <div className="batch-export-list" aria-label="Scripts to export">
        {results.map(({ script, preview }) => (
          <label className={`batch-export-script ${selected.has(script.id) ? "is-selected" : ""}`} key={script.id}>
            <input type="checkbox" aria-label={script.title || "Untitled script"} data-help={`Include ${script.title || "this script"} in the export.`}
              checked={selected.has(script.id)} disabled={busy} onChange={event => {
                const checked = event.target.checked;
                setSelected(previous => { const next = new Set(previous); if (checked) next.add(script.id); else next.delete(script.id); return next; });
              }} />
            <span>
              <strong>{script.title || "Untitled script"}</strong>
              <small>{script.tags.length ? script.tags.join(" · ") : "Unfiled"}</small>
              {preview && <small className="batch-export-excerpt">{preview.before}<mark>{preview.match}</mark>{preview.after}</small>}
            </span>
          </label>
        ))}
        {!results.length && <p className="batch-export-empty">{scripts.length ? "No scripts match. Try another word or clear your search." : "Your library is empty. Add a script before exporting."}</p>}
      </div>
      <footer className="batch-export-footer">
        <fieldset disabled={busy} className="batch-export-formats">
          <legend>File format</legend>
          <label>
            <input type="radio" name={formatId} value="docx" checked={format === "docx"} onChange={() => setFormat("docx")}
              aria-label="Microsoft Word (.docx)" data-help="Export editable Word documents with text styles, colors, word sizes, alignment and lists preserved." />
            <span><strong>Microsoft Word (.docx)</strong><small>Keeps your script formatting</small></span>
          </label>
          <label>
            <input type="radio" name={formatId} value="txt" checked={format === "txt"} onChange={() => setFormat("txt")}
              aria-label="Plain text (.txt)" data-help="Export the script text and line breaks without formatting." />
            <span><strong>Plain text (.txt)</strong><small>Text without formatting</small></span>
          </label>
        </fieldset>
        <p className="batch-export-delivery">{count > 1 ? "One file per script, downloaded together in a ZIP." : "One script downloads as a file. Multiple scripts come in a ZIP."}</p>
        {error && <p role="alert" className="batch-export-error">{error}</p>}
        {progress && <p role="status" className="batch-export-progress">{progress.done === progress.total ? "Packing your download…" : `Preparing scripts · ${progress.done} of ${progress.total}`}</p>}
        <div className="batch-export-actions">
          <button type="button" className="subtle-button" data-help="Close this dialog and cancel any export still being prepared." onClick={onClose}>Cancel</button>
          <button type="button" className="primary-button" disabled={!count || busy} onClick={() => { void exportSelected(); }}
            data-help={!count ? "Select at least one script to export." : `Download ${count} selected ${count === 1 ? "script" : "scripts"} in ${format === "docx" ? "Word" : "plain text"} format. Exporting keeps your live take running.`}>
            <Download size={15} aria-hidden="true" />
            {busy ? "Preparing export…" : `Export ${count} ${count === 1 ? "script" : "scripts"}`}
          </button>
        </div>
      </footer>
    </dialog>
  );
}
